import type { PlaycastStreamDimensions } from '@playcastdotio/CoreTypes';
import type {
  PlaycastMessage,
  PlaycastMessageSource,
  PlaycastMessageStreamAddSink,
  PlaycastMessageStreamAttachParticipant,
  PlaycastMessageStreamCreate,
  PlaycastMessageStreamDetachParticipant,
  PlaycastMessageStreamEnumerateAll,
  PlaycastMessageStreamGetFrameOutputs,
  PlaycastMessageStreamGetStreamSinks,
  PlaycastMessageStreamList,
  PlaycastMessageStreamRemove,
  PlaycastMessageStreamRemoveSink,
  PlaycastMessageStreamSetDimensions,
  PlaycastMessageStreamSetFrameOutput,
  PlaycastMessageStreamSetSinkTypeEnabled,
  RegistryId,
  StreamComposition,
  StreamId,
  StreamParticipantRole,
} from '../types';
import { formatMessage } from './_util/formatMessage';

const target = 'stream';
const hostId = 'experimental';
const messageSource: PlaycastMessageSource = 'host';

export const setDimensions = (
  hostId: string,
  dimensions: PlaycastStreamDimensions,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageStreamSetDimensions> => {
  const message: PlaycastMessageStreamSetDimensions = {
    target,
    action: 'setDimensions',
    isReply: false,
    message: dimensions,
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamSetDimensions>;
};

// --- Enumeration ---
export const enumerateAll = (userAuth?: string): PlaycastMessage<PlaycastMessageStreamEnumerateAll> => {
  const message: PlaycastMessageStreamEnumerateAll = {
    target,
    action: 'enumerateAll',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamEnumerateAll>;
};

export const getFrameOutputs = (userAuth?: string): PlaycastMessage<PlaycastMessageStreamGetFrameOutputs> => {
  const message: PlaycastMessageStreamGetFrameOutputs = {
    target,
    action: 'getFrameOutputs',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamGetFrameOutputs>;
};

export const getStreamSinks = (userAuth?: string): PlaycastMessage<PlaycastMessageStreamGetStreamSinks> => {
  const message: PlaycastMessageStreamGetStreamSinks = {
    target,
    action: 'getStreamSinks',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamGetStreamSinks>;
};

export const list = (userAuth?: string): PlaycastMessage<PlaycastMessageStreamList> => {
  const message: PlaycastMessageStreamList = {
    target,
    action: 'list',
    isReply: false,
    message: {},
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamList>;
};

// --- Lifecycle ---
export const create = (
  composition: StreamComposition,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageStreamCreate> => {
  const message: PlaycastMessageStreamCreate = {
    target,
    action: 'create',
    isReply: false,
    message: {
      frameOutput: composition.frameOutput ?? 0,
      sinks: composition.sinks ?? [],
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamCreate>;
};

export const remove = (streamId: StreamId, userAuth?: string): PlaycastMessage<PlaycastMessageStreamRemove> => {
  const message: PlaycastMessageStreamRemove = {
    target,
    action: 'remove',
    isReply: false,
    message: { streamId },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamRemove>;
};

// --- Modify (all keyed by streamId) ---
export const setFrameOutput = (
  streamId: StreamId,
  frameOutput: RegistryId,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageStreamSetFrameOutput> => {
  const message: PlaycastMessageStreamSetFrameOutput = {
    target,
    action: 'setFrameOutput',
    isReply: false,
    message: { streamId, frameOutput },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamSetFrameOutput>;
};

export const addSink = (
  streamId: StreamId,
  sink: RegistryId,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageStreamAddSink> => {
  const message: PlaycastMessageStreamAddSink = {
    target,
    action: 'addSink',
    isReply: false,
    message: { streamId, sink },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamAddSink>;
};

export const removeSink = (
  streamId: StreamId,
  sink: RegistryId,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageStreamRemoveSink> => {
  const message: PlaycastMessageStreamRemoveSink = {
    target,
    action: 'removeSink',
    isReply: false,
    message: { streamId, sink },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamRemoveSink>;
};

// Per-type mute for a sink (does NOT change what the sink can carry; supportedTypes is fixed).
// Meant for multi-type sinks - gate one type (e.g. an A/V camera's audio) while the others keep
// flowing. For a single-type sink this duplicates removeSink, so prefer composition there.
export const setSinkTypeEnabled = (
  registryId: RegistryId,
  type: number,
  enabled: boolean,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageStreamSetSinkTypeEnabled> => {
  const message: PlaycastMessageStreamSetSinkTypeEnabled = {
    target,
    action: 'setSinkTypeEnabled',
    isReply: false,
    message: { registryId, type, enabled },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamSetSinkTypeEnabled>;
};

/**
 * Grant roles on a stream. For android multi-instance, pass gameInstanceIndex >= 0 with the
 * host-facing launch streamId so Playjector resolves the per-instance stream (host never learns
 * internal stream ids). userId must match header.user.id on subsequent player input (peerId).
 */
export const attachParticipant = (
  streamId: StreamId,
  userId: string,
  roles: StreamParticipantRole[],
  gameInstanceIndex?: number,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageStreamAttachParticipant> => {
  const message: PlaycastMessageStreamAttachParticipant = {
    target,
    action: 'attachParticipant',
    isReply: false,
    message: {
      streamId,
      userId,
      roles,
      ...(gameInstanceIndex !== undefined ? { gameInstanceIndex } : {}),
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamAttachParticipant>;
};

export const detachParticipant = (
  streamId: StreamId,
  userId: string,
  gameInstanceIndex?: number,
  userAuth?: string,
): PlaycastMessage<PlaycastMessageStreamDetachParticipant> => {
  const message: PlaycastMessageStreamDetachParticipant = {
    target,
    action: 'detachParticipant',
    isReply: false,
    message: {
      streamId,
      userId,
      ...(gameInstanceIndex !== undefined ? { gameInstanceIndex } : {}),
    },
  };
  return formatMessage(hostId, messageSource, message, userAuth) as PlaycastMessage<PlaycastMessageStreamDetachParticipant>;
};
